import { LocationApi } from '@deckee/api-app-main';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AppAsyncThunkAPI } from '../..';
import apiConfiguration from '../../../backend/apiConfiguration';
import { PlanEmergencyContact, PlanPoint } from './models';
import { selectPointById } from './selectors';
import { selectCurrentMemberId } from '../backend-service';
import { getExistingFloatPlanContacts } from '../../../member/profile';
import { callEndpoint, checkClientToken } from '../../../backend';

export const setWaypoint = createAction(
  '[Plan] set waypoint',
  (latitude, longitude, name, ordinal) => ({
    payload: { latitude, longitude, name, ordinal },
  }),
);

export const addPoint = createAction(
  '[Plan] add point',
  (id: string, latitude?: number, longitude?: number) => ({
    payload: { latitude, longitude, id },
  }),
);

export const removePoint = createAction(
  '[Plan] remove point',
  (id: string) => ({
    payload: id,
  }),
);

export const setPlanIsLoggingOn = createAction(
  '[Plan] set plan is logging on',
  (isLoggingOn: boolean) => ({
    payload: isLoggingOn,
  }),
);

export const setPlanHasDiving = createAction(
  '[Plan] set plan has a dive',
  (isDiving: boolean) => ({
    payload: isDiving,
  }),
);

export const updatePoint = createAction(
  '[Plan] update point',
  (payload: {
    id: string;
    latitude: number | string;
    longitude: number | string;
    isRoundTrip?: boolean;
    name?: string;
  }) => ({
    payload,
  }),
);

export const updatePointWithPlaceId = createAsyncThunk<
  void,
  { id: string; placeId: string; name: string },
  AppAsyncThunkAPI
>(
  '[Plan] update point with place id',
  async (
    { id, placeId, name },
    { getState, dispatch, rejectWithValue, signal },
  ) => {
    const pointToUpdate = selectPointById(id)(getState());

    const api = new LocationApi(apiConfiguration);
    if (!pointToUpdate) {
      return rejectWithValue('Point not found');
    }

    try {
      signal?.throwIfAborted();
      const { data } = await api.locationsRetrieveDataForPlaceId({
        locationsRetrieveDataForPlaceIdRequest: {
          fields: ['geometry', 'address_component'],
          placeId,
        },
      });

      const { lat, lon } = data;

      dispatch(
        updatePoint({
          ...pointToUpdate,
          latitude: lat,
          longitude: lon,
          name,
        }),
      );
    } catch (e: any) {
      return rejectWithValue(e?.message);
    }

    return undefined;
  },
);

// Set the list of selected contacts on a plan
export const setEmergencyContacts = createAction(
  '[Plan] set emergency contacts',
  (contacts: PlanEmergencyContact[]) => ({
    payload: contacts,
  }),
);

// This action is to update the list of contacts. It differs from `setEmergencyContacts` as that action assumes
// the list of contacts are selected
export const setContacts = createAction(
  '[Plan] set contacts',
  (contacts: PlanEmergencyContact[]) => ({
    payload: contacts,
  }),
);

export const addOrUpdateEmergencyContact = createAction(
  '[Plan] add or update emergency contact',
  (contact: PlanEmergencyContact) => ({
    payload: contact,
  }),
);

export const removeSelectedEmergencyContact = createAction(
  '[Plan] remove emergency contact',
  (id: PlanEmergencyContact['id']) => ({
    payload: id,
  }),
);

export const setPoints = createAction(
  '[Plan] set points',
  (planPoints: PlanPoint[]) => ({
    payload: planPoints,
  }),
);
export const reset = createAction('[Plan] reset');

export const fetchExistingEmergencyContacts = createAsyncThunk<
  void,
  void,
  AppAsyncThunkAPI
>(
  '[plan] fetch emergency contacts for current member',
  async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState();

    const currentMemberId = selectCurrentMemberId(state);

    if (!currentMemberId) {
      rejectWithValue('No member id found');
      return;
    }

    let emergencyContacts: PlanEmergencyContact[];
    try {
      emergencyContacts = await getExistingFloatPlanContacts(currentMemberId);
    } catch (e: any) {
      rejectWithValue(e.message);
      return;
    }

    await dispatch(setContacts(emergencyContacts));
  },
);

export const updateMemberContactInformation = createAsyncThunk<
  void,
  { fullName: string; phoneNumber: string },
  AppAsyncThunkAPI
>(
  '[plan] update member phone number',
  async ({ fullName, phoneNumber }, { getState, rejectWithValue }) => {
    const state = getState();

    const currentMemberId = selectCurrentMemberId(state);

    if (!currentMemberId) {
      rejectWithValue('No member id found');
      return;
    }

    try {
      await callEndpoint(
        callEndpoint.endpoints.updateMemberContactInformation,
        {
          fullName,
          phoneNumber,
        },
      );
      await checkClientToken();
    } catch (e: any) {
      rejectWithValue(e.message);
    }
  },
);
